import { resolveFormTenantId } from "../../utils/tenantContext";
import { zodResolver } from '@hookform/resolvers/zod';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useForm } from "react-hook-form";
import { toast } from 'react-hot-toast';
import CloseButton from "../../components/Application/CloseButton";
import { useApplicationFormLogic } from '../../hooks/useApplicationFormLogic';
import EvaluationFormHeader from "../../pages/EvaluationNew/components/EvaluationFormHeader";
import { request } from '../../utils/api';
import { getLocalSystemDate, parseIsoDateToMDYUS } from '../../utils/dateUtils';
import { buildPayloadWithIsoDates } from '../../utils/applicationFormPayload';
import { getStandardizedSignatures } from '../../utils/formHelpers';
import { governmentFormSchema } from './application.validation';

const I9Form = ({ positionName, onClose }) => {
  const [i9Id, setI9Id] = useState(null);


  const methods = useForm({
    resolver: zodResolver(governmentFormSchema),
    defaultValues: {
      tenantId: undefined,
      userId: undefined,
      applicantName: "",
      applicantSignature: "",
      applicantSignedAt: parseIsoDateToMDYUS(getLocalSystemDate()),
      reviewedBySignature: "",
      reviewedBySignedAt: "",
      reviewedBySignedBy: ""
    },
    mode: "onChange"
  });
  const [currentStatus, setCurrentStatus] = useState("");

  const onSubmit = async (data, flag = 'send_to_office', additionalData = {}) => {
    const actualFlag = typeof flag === 'string' ? flag : 'send_to_office';
    try {
      const payload = buildPayloadWithIsoDates(
        data,
        additionalData,
        ['applicantSignedAt', 'reviewedBySignedAt'],
        { flag: actualFlag }
      );
      payload.positionAppliedFor = positionName;

      // Handle To Be Corrected comments
      const finalComments = additionalData?.comments || data?.comments || methods.getValues("comments");
      if (finalComments) {
        payload.comments = finalComments;
      }

      const method = i9Id ? 'PUT' : 'POST';
      const url = i9Id ? `/i9-forms/${i9Id}` : '/i9-forms';

      const response = await request(url, {
        method,
        body: JSON.stringify(payload)
      });

      if (response.status === 1) {
        toast.success(response.message || "I-9 Form processed successfully!");
        closeForm();
      } else {
        toast.error(response.message || "Failed to process I-9 Form");
      }
    } catch (error) {
      console.error("Error submitting I-9:", error);
      toast.error("Failed to submit form");
    }
  };

  const {
    targetUserId,
    closeForm,
    user: currentUserData,
    isAdmin,
    actualFromId
  } = useApplicationFormLogic({
    methods,
    applicantNameField: "applicantName",
    applicantSignatureDateField: "applicantSignedAt",
    applicantSignatureField: "applicantSignature",
    adminSignatureField: "reviewedBySignature",
    adminSignatureDateField: "reviewedBySignedAt",
    onClose,
    onSubmit,
    currentStatus
  });

  const { reset } = methods;

  useEffect(() => {
    const fetchData = async () => {
      // Must wait for currentUserData
      if (!currentUserData) return;
      if (isAdmin && !targetUserId && !i9Id) return;

      try {
        if (!actualFromId && !targetUserId && !i9Id) {
          if (currentUserData && !isAdmin) {
            reset({
              tenantId: resolveFormTenantId(currentUserData?.tenantId),
              userId: currentUserData.id,
              applicantName: `${currentUserData.firstName || ''} ${currentUserData.lastName || ''}`.trim(),
              positionAppliedFor: positionName || "",
              applicantSignedAt: parseIsoDateToMDYUS(getLocalSystemDate()),
            });
          }
          return;
        }

        let response;
        if (isAdmin) {
          if (actualFromId) {
            response = await request(`/i9-forms/${actualFromId}?userId=${targetUserId}`);
          } else {
            response = await request(`/i9-forms?userId=${targetUserId}`);
          }
        } else {
          response = await request(`/i9-forms/${actualFromId || targetUserId}`);
        }

        if (response.status === 1 && response.result) {
          const data = response.result.i9Form || response.result;
          setI9Id(data.id);
          setCurrentStatus(data.status || "");
          reset({
            ...data,
            applicantName: data.applicantName || "",
            applicantSignature: data.applicantSignature || "",
            ...getStandardizedSignatures(data, isAdmin)
          });
        }
      } catch (error) {
        console.error("Error fetching I9 data:", error);
      }
    };
    fetchData();
  }, [targetUserId, reset, currentUserData, isAdmin, i9Id, actualFromId, positionName]);


  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <CloseButton onClose={closeForm} />

      <div className="max-w-6xl mx-auto bg-white shadow-xl overflow-hidden">
        <EvaluationFormHeader
          title="I-9 Form"
          subtitle="Employment Eligibility Verification"
        />
        {/* PDF Viewer */}
        <div className="mb-8 border rounded-xl overflow-hidden  shadow-sm h-[800px] p-8">
          <iframe
            src="/I-9.pdf#toolbar=1"
            className="w-full h-full"
            title="I-9 Form"
          />
        </div>

      </div>

    </div>
  );
};

I9Form.propTypes = {
  positionName: PropTypes.string,
  onClose: PropTypes.func
};

export default I9Form;

